import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import { NAV, SITE } from "@/data/site";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAFAFA]/85 border-b border-[#E5E7EB]/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="flex h-20 items-center justify-between gap-6">
          <Logo />

          <nav
            data-testid="primary-nav"
            className="hidden lg:flex items-center gap-1"
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                    isActive
                      ? "text-[#082133]"
                      : "text-[#4B5563] hover:text-[#082133]"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="inline-flex items-center">
                    {t(item.labelKey)}
                    {isActive && (
                      <span className="absolute -bottom-0.5 left-4 right-4 h-[2px] rounded-full bg-[#082133]" />
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language toggle */}
            <div
              data-testid="lang-toggle-container"
              className="hidden sm:inline-flex items-center rounded-full border border-[#082133]/15 bg-white/70 backdrop-blur px-1 py-1 text-[11px] font-semibold tracking-wider"
              role="group"
              aria-label="Language toggle"
            >
              <Globe className="h-3.5 w-3.5 ml-2 mr-1 text-[#082133]" />
              <button
                type="button"
                data-testid="lang-toggle-en"
                onClick={() => setLang("en")}
                className={`px-3 py-1 rounded-full transition-all ${
                  lang === "en"
                    ? "bg-[#082133] text-[#FAFAFA]"
                    : "text-[#4B5563] hover:text-[#082133]"
                }`}
                aria-pressed={lang === "en"}
              >
                EN
              </button>
              <span className="mx-0.5 text-[#082133]/30">|</span>
              <button
                type="button"
                data-testid="lang-toggle-es"
                onClick={() => setLang("es")}
                className={`px-3 py-1 rounded-full transition-all ${
                  lang === "es"
                    ? "bg-[#082133] text-[#FAFAFA]"
                    : "text-[#4B5563] hover:text-[#082133]"
                }`}
                aria-pressed={lang === "es"}
              >
                ES
              </button>
            </div>

            <a
              href={SITE.phoneHref}
              data-testid="header-call-link"
              className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[#111111] hover:text-[#082133] transition-colors px-3 py-2"
            >
              <Phone className="h-4 w-4" />
              {SITE.phone}
            </a>

            <Link to="/contact" className="hidden sm:inline-flex">
              <Button
                data-testid="header-book-btn"
                className="rounded-full bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47] px-5 h-11 text-sm font-medium tracking-wide shadow-[0_10px_25px_-12px_rgba(8,33,51,0.5)]"
              >
                {t("nav.bookNow")}
              </Button>
            </Link>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="lg:hidden p-2.5 rounded-full border border-[#082133]/15 text-[#082133] hover:bg-[#082133]/5 transition"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="lg:hidden border-t border-[#E5E7EB] bg-[#FAFAFA]"
        >
          <div className="px-5 sm:px-8 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${
                  item.to.replace(/\//g, "") || "home"
                }`}
                className={({ isActive }) =>
                  `block rounded-2xl px-4 py-3 text-base font-medium ${
                    isActive
                      ? "bg-[#082133] text-[#FAFAFA]"
                      : "text-[#111111] hover:bg-[#082133]/5"
                  }`
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}

            <div className="mt-4 flex items-center justify-between rounded-2xl border border-[#082133]/15 bg-white px-4 py-3">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#4B5563]">
                <Globe className="h-3.5 w-3.5" />
                Language
              </span>
              <div className="inline-flex items-center text-xs font-semibold">
                <button
                  type="button"
                  data-testid="mobile-lang-en"
                  onClick={() => setLang("en")}
                  className={`px-3 py-1.5 rounded-full transition ${
                    lang === "en"
                      ? "bg-[#082133] text-[#FAFAFA]"
                      : "text-[#4B5563]"
                  }`}
                >
                  EN
                </button>
                <span className="mx-1 text-[#082133]/30">|</span>
                <button
                  type="button"
                  data-testid="mobile-lang-es"
                  onClick={() => setLang("es")}
                  className={`px-3 py-1.5 rounded-full transition ${
                    lang === "es"
                      ? "bg-[#082133] text-[#FAFAFA]"
                      : "text-[#4B5563]"
                  }`}
                >
                  ES
                </button>
              </div>
            </div>

            <a
              href={SITE.phoneHref}
              data-testid="mobile-call-link"
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-2xl bg-white border border-[#082133]/15 px-4 py-3 text-sm font-medium text-[#111111]"
            >
              <Phone className="h-4 w-4" />
              {SITE.phone}
            </a>
            <Link
              to="/contact"
              data-testid="mobile-book-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#082133] px-4 py-3 text-sm font-medium text-[#FAFAFA]"
            >
              {t("nav.bookNow")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
